// PPM payments tracker (/office/ppm-payments, Session AAS, Option C). Every upcoming PPM
// service visit with a labelled status pill combining its payment health (from the covering
// contract invoice) and its scheduling state, escalating to red within RUNWAY_DAYS of the
// service due date when still unpaid. Reads GET /api/scheduling/ppm-payments. Visits stay
// scheduled (the live calendar is never frozen); this is a payment-health overlay, not a gate.
import { useEffect, useMemo, useState } from "react";
import { apiRequest } from "../../lib/api";
import { ppmPaymentPill, DEFAULT_RUNWAY_DAYS, type PaymentState, type PillTone } from "../../lib/ppmPaymentPill";
import { ppmPaymentEntry } from "../../lib/statusRegistry";
import { StatusPill } from "../../components/StatusPill";
import { EmptyState } from "../../components/EmptyState";
import { formatDate } from "../../lib/format";
import { PageHeader } from "../../components/PageHeader";
import { Loader } from "../../components/Loader";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";

interface PpmPaymentVisit {
  jobId: string;
  ele: string;
  siteName: string;
  customerName: string;
  scheduledFor: string | null;
  jobStatus: string;
  paymentState: PaymentState;
}

// Outline means an office action is needed; tint means settled. The registry converts the pill's
// answer (ppmPaymentEntry) and StatusPill draws it; the pill always shows a WORD, so colour is never
// the only signal (house rule).
export function PaymentStatus({ visit, now, runwayDays }: { visit: PpmPaymentVisit; now: Date; runwayDays?: number }) {
  const pill = ppmPaymentPill({ jobStatus: visit.jobStatus, scheduledFor: visit.scheduledFor, paymentState: visit.paymentState, now, runwayDays });
  const entry = ppmPaymentEntry(pill);
  return <StatusPill word={entry.label} tone={entry.tone} variant={entry.variant} title={pill.outline ? "Office action needed" : "Settled"} />;
}

// M-10, Session AQU: search and sort. Search matches the lift ID, site and customer. The status
// column sorts by urgency (Payment overdue first when ascending), read from the same pill the row
// shows, so the order always agrees with the tenant's runway.
const PPM_SEARCH_ACCESSORS: SearchAccessors<PpmPaymentVisit> = [(v) => v.ele, (v) => v.siteName, (v) => v.customerName];
const PPM_SORT_OPTIONS = [
  { key: "ele", label: "Lift ID" },
  { key: "site", label: "Site" },
  { key: "customer", label: "Customer" },
  { key: "due", label: "Service due" },
  { key: "status", label: "Status" },
];
const URGENCY_BY_TONE: Record<PillTone, number> = { red: 0, amber: 1, blue: 2, grey: 3, green: 4 };

function ppmSortAccessors(now: Date, runwayDays: number): SortAccessors<PpmPaymentVisit> {
  return {
    ele: (v) => v.ele,
    site: (v) => v.siteName,
    customer: (v) => v.customerName,
    due: (v) => {
      if (!v.scheduledFor) return null;
      const t = new Date(v.scheduledFor).getTime();
      return Number.isNaN(t) ? null : t;
    },
    status: (v) =>
      URGENCY_BY_TONE[
        ppmPaymentPill({ jobStatus: v.jobStatus, scheduledFor: v.scheduledFor, paymentState: v.paymentState, now, runwayDays }).tone
      ],
  };
}

export function PpmPaymentsPage() {
  const [visits, setVisits] = useState<PpmPaymentVisit[] | null>(null);
  // The overdue runway is tunable per tenant (Session AAT Loop 1); the server resolves it and the
  // page applies it to every pill. Falls back to the default until the response arrives.
  const [runwayDays, setRunwayDays] = useState<number>(DEFAULT_RUNWAY_DAYS);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    void (async () => {
      try {
        const data = await apiRequest<{ visits: PpmPaymentVisit[]; runwayDays?: number }>("/scheduling/ppm-payments");
        if (active) {
          setVisits(data.visits);
          if (typeof data.runwayDays === "number") setRunwayDays(data.runwayDays);
          setState("ready");
        }
      } catch {
        if (active) setState("error");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const now = useMemo(() => new Date(), [visits]);
  const actionNeeded = useMemo(
    () => (visits ?? []).filter((v) => ppmPaymentPill({ jobStatus: v.jobStatus, scheduledFor: v.scheduledFor, paymentState: v.paymentState, now, runwayDays }).outline).length,
    [visits, now, runwayDays],
  );

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("ppm-payments");
  const shown = sortRows(filterRows(visits ?? [], search, PPM_SEARCH_ACCESSORS), ppmSortAccessors(now, runwayDays), sort);

  return (
    <div>
      <PageHeader
        title="PPM payments"
        className="mb-4"
        subtitle={
          <>
            Upcoming planned maintenance visits with the payment status of their covering contract invoice. A visit still
            unpaid within {runwayDays} days of its service date is flagged Payment overdue. Visits are never held: this is
            a payment-health view, not a scheduling block.
          </>
        }
      />

      {state === "ready" && visits && visits.length > 0 && (
        <ListToolbar
          className="mb-3"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search lift ID, site or customer..."
          searchLabel="Search PPM payments"
          resultCount={{ shown: shown.length, total: visits.length, noun: "visits" }}
          sort={{ state: sort, options: PPM_SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {state === "loading" ? (
        <Loader rows={6} />
      ) : state === "error" ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the PPM payments. Please try again in a moment.
        </p>
      ) : visits && visits.length === 0 ? (
        <EmptyState message="No upcoming PPM visits. Generate the PPM schedule from Scheduling to populate this view." />
      ) : (
        <>
          <p className="mb-3 text-sm text-ele-body">
            {visits!.length} upcoming visit{visits!.length === 1 ? "" : "s"}
            {actionNeeded > 0 ? (
              <span className="text-amber-300"> · {actionNeeded} need{actionNeeded === 1 ? "s" : ""} office action</span>
            ) : (
              <span className="text-ele-green"> · all clear</span>
            )}
          </p>
          {shown.length === 0 ? (
            <EmptyState role="status" message={`No visits match "${search.trim()}".`} />
          ) : (
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent bg-ele-surface">
            <table className="ele-list-table text-left text-sm">
              <colgroup>
                <col style={{ width: "15%" }} />
                <col className="max-md:hidden" style={{ width: "26%" }} />
                <col className="max-md:hidden" style={{ width: "24%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "19%" }} />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <SortableHeader label="Lift ID" colKey="ele" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Site" colKey="site" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                  <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                  <SortableHeader label="Service due" colKey="due" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                </tr>
              </thead>
              <tbody>
                {/* No detail page for a PPM payment row (a pure payment-health view), so there is
                    no row tap or chevron here; mobile keeps the lift, service date and status. */}
                {shown.map((v) => (
                  <tr key={v.jobId} className="border-t border-ele-purple-accent">
                    <td className="px-3 py-2 align-top font-heading text-ele-green">{v.ele}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">{v.siteName}</td>
                    <td className="max-md:hidden px-3 py-2 align-top">{v.customerName}</td>
                    <td className="px-3 py-2 align-top">{v.scheduledFor ? formatDate(v.scheduledFor) : "Not scheduled"}</td>
                    <td className="px-3 py-2 align-top">
                      <PaymentStatus visit={v} now={now} runwayDays={runwayDays} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}
